/**
 * Creative option lists: genres, vocals, Studio styles, Genre Mixer choices,
 * and the Music DNA vocabularies.
 *
 * MOCK/FIXTURE DATA, moved verbatim from the monolith. These drive the option
 * chips and selects; a backend would eventually serve them so the vocabulary
 * can change without a frontend deploy.
 */

export const GENRES = [
  'Acoustic pop',
  'Synthwave',
  'Lo-fi instrumental',
  'Cinematic',
  'Hip hop',
  'Afrobeat',
  'Rock',
  'Pop',
  'R&B',
  'Country',
  'Jazz',
  'Blues',
  'Classical',
  'Reggae',
  'Soul',
  'Gospel',
  'Amapiano',
];
export const VOCALS = ['Male vocal', 'Female vocal', 'Instrumental only'];

export const REMIX_STYLES = [
  'Dance',
  'Acoustic',
  'Emotional',
  'Epic',
  'Afrobeats',
  'Electronic',
  'Rock',
];
export const COVER_STYLES = [
  'Realistic',
  'Anime',
  'Minimal',
  'Retro',
  'Modern',
  'Neon',
  'Cinematic',
];
export const EXTEND_LENGTHS = ['15 sec', '30 sec', '60 sec'];
export const PLAYBACK_SPEEDS = [0.5, 1, 1.5, 2];

// ---- Genre Mixer (Composer+) ----
export const GM_GENRES = [
  'Afrobeats',
  'Pop',
  'Rock',
  'Jazz',
  'Hip Hop',
  'Country',
  'R&B',
  'EDM',
  'House',
  'Amapiano',
  'Latin',
  'Dancehall',
  'Reggae',
  'Classical',
  'Lo-Fi',
  'Synthwave',
  'Cinematic',
  'Country Pop',
  'Gospel',
  'Blues',
];
export const GM_MOODS = [
  'Happy',
  'Emotional',
  'Dark',
  'Romantic',
  'Energetic',
  'Epic',
  'Calm',
  'Dreamy',
  'Hopeful',
  'Aggressive',
];
export const GM_TEMPOS = ['Slow', 'Medium', 'Fast'];
/**
 * Languages a song can be sung in.
 *
 * Named for the Genre Mixer because that is where the list started, and shared with
 * the composer's own language picker rather than copied — see LANGUAGES below. A
 * second list would drift, and the two screens post to the same `language` field on
 * the same endpoint (docs/api-contract.md, POST /generations).
 */
export const GM_LANGUAGES = [
  'English',
  'Spanish',
  'French',
  'Portuguese',
  'German',
  'Italian',
  'Japanese',
  'Korean',
  'Hindi',
  'Arabic',
  'Instrumental',
];
export const GM_VOCAL_TYPES = ['Male', 'Female', 'Duet', 'Choir', 'Instrumental'];

/**
 * The composer's language options — the mixer's list without "Instrumental".
 *
 * Derived rather than retyped, so adding a language serves both screens at once.
 *
 * "Instrumental" is dropped because on /create it would be a second control saying the
 * same thing: the Vocals picker already offers "Instrumental only", and a song set to
 * Instrumental vocals with a language of Japanese — or the reverse — is a state the user
 * can reach and the request cannot express. The mixer keeps it because its own vocal
 * list is a different vocabulary (GM_VOCAL_TYPES) that it pairs with this one.
 *
 * Filtered by value, not by index, so a reordering of the list above cannot silently
 * remove the wrong entry.
 */
export const OTHER_LANGUAGE = 'Others';
export const LANGUAGES = [...GM_LANGUAGES.filter((l) => l !== 'Instrumental'), OTHER_LANGUAGE];

/**
 * The language a new song is in unless the user picks otherwise.
 *
 * English, which is what generationApi has always stamped on every generated song, so
 * making the field selectable changes nothing for a user who does not touch it.
 */
export const DEFAULT_LANGUAGE = 'English';
export const GM_LENGTHS = ['2 min', '3 min'];
export const GM_VARIATIONS = ['Commercial Radio', 'Festival Mix', 'Acoustic Version'];
export const GM_STRUCTURES = [
  'Intro – Verse – Chorus – Verse – Chorus – Bridge – Chorus – Outro',
  'Intro – Build – Drop – Break – Drop – Outro',
  'Verse – Pre-Chorus – Chorus (repeating, minimal bridge)',
];
export const GM_AUDIENCES = [
  'Afrobeats & Amapiano fans, 18–34',
  'Pop radio listeners, 16–30',
  'Late-night playlist listeners, 20–35',
  'Festival and dance-floor crowds, 18–28',
  'Coffee-shop & study playlist listeners, all ages',
];

// ---- Music DNA vocabularies ----
export const DNA_GENRES = [
  'Synth-pop',
  'Alternative R&B',
  'Indie folk',
  'Melodic hip hop',
  'Dream pop',
  'Afrobeats',
  'Cinematic pop',
  'Deep house',
];
export const DNA_MOODS = [
  'Euphoric',
  'Melancholic',
  'Nostalgic',
  'Triumphant',
  'Dreamy',
  'Restless',
  'Tender',
  'Defiant',
];
export const DNA_VOCALS = [
  'Airy falsetto',
  'Raspy alto',
  'Smooth tenor',
  'Layered harmonies',
  'Spoken-word verses',
  'Soulful belting',
];
export const DNA_PRODUCTION = [
  'Lo-fi and warm',
  'Polished and radio-ready',
  'Minimal and spacious',
  'Dense and layered',
  'Analog and vintage',
  'Glossy and modern',
];
export const DNA_INSTRUMENTS = [
  '808 sub bass',
  'acoustic guitar',
  'analog synth pads',
  'live strings',
  'vinyl crackle',
  'soft piano',
  'hand percussion',
  'brass stabs',
];
export const DNA_ATMOSPHERES = [
  'late-night city lights',
  'a sun-drenched afternoon',
  'rain against a window',
  'an empty highway at dusk',
  'a crowded dance floor',
  'a quiet bedroom confessional',
];

export const DEMO_VIDEO_URL = 'https://fast.wistia.com/embed/medias/gvrjsetlgv';
